import type { QueryView } from '@krp/client-runtime'

import { readyData } from '../../../shared/model/query'
import { accessKeyOf, type CashierAccessState } from './cashierAccessSlice'
import type { OpeningEntry } from './openingSlice'
import { cashierKeyOf, type StartMemory } from './startSlice'
import type {
  AqsiCashierRmkSettings,
  AqsiPosCashiers,
  CashierOptionVm,
  CodeVm,
  LoginKind,
  MineVm,
  OpeningStepVm,
  OpeningVm,
  OperationVm,
  PriceTypeOptionVm,
  PriceTypesVm,
  RegisterRowVm,
  ShiftVm,
  StartVm,
} from './types'

/** Сколько кассиров показывать без поиска (RP-FB22 3): дальше появляется поле «Найти кассира». */
export const CASHIER_SEARCH_FROM = 8

/** Ключ «Цены кассы» в выборе вида цены (тот же, что в `selectors.ts`; здесь — чтобы не зациклить модули). */
const PRICE_CASH = 'cash'

function loginKindOf(): LoginKind {
  return 'pin'
}

const isReady = (view: QueryView) => view.status === 'ready' || view.status === 'empty'

function missingCashierNote(name: string): string {
  return name === ''
    ? 'Прежнего кассира больше нет в кабинете — выберите кассира.'
    : `Кассира «${name}» больше нет в кабинете — выберите кассира.`
}

/** Выбор вида цены для «Начала работы»: список общего каталога и значение по умолчанию (последний у этого кассира). */
export interface StartPriceInput {
  options: PriceTypesVm['options']
  query: QueryView
  ready: boolean
}

export interface StartInput {
  resourceId: string
  /** Персональные настройки РМК выбранного кассира (RP-CT6); нет — прежний выбор «как в прошлый раз». */
  rmk?: AqsiCashierRmkSettings | undefined
  cashiersView: QueryView
  cashiers: AqsiPosCashiers | undefined
  memory: StartMemory
  access: CashierAccessState
  /** Список видов цен в виде, пригодном для выбора (без выбранного: выбранное считает `buildStart`). */
  price: StartPriceInput
  registersQuery: QueryView
  rows: readonly RegisterRowVm[]
  busy: boolean
}

function cashierOptions(data: AqsiPosCashiers, selectedId: string | null, lastId: string | null): CashierOptionVm[] {
  return [...data.cashiers]
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    .map((cashier) => ({
      id: cashier.cashierId,
      name: cashier.name,
      position: cashier.position,
      login: loginKindOf(),
      selected: selectedId !== null && cashier.cashierId.toLowerCase() === selectedId.toLowerCase(),
      lastTime: lastId !== null && cashier.cashierId.toLowerCase() === lastId.toLowerCase(),
    }))
}

function codeOf(
  resourceId: string,
  cashier: { id: string; name: string; login: LoginKind },
  access: CashierAccessState,
): CodeVm | null {
  const key = accessKeyOf(resourceId, cashier.id)
  const base = { cashierId: cashier.id, cashierName: cashier.name, attemptsLeft: null, lockedUntil: null, message: null, expiresAt: null }
  const proof = access.proofs[key]
  if (proof !== undefined) return { ...base, state: 'verified', expiresAt: proof.expiresAt }
  const lock = access.locks[key]
  if (lock !== undefined) {
    return { ...base, state: 'locked', lockedUntil: lock, message: 'Слишком много неверных кодов: кассир заблокирован.' }
  }
  const check = access.checks[key]
  if (check === undefined) return { ...base, state: 'needed' }
  return { ...base, state: check.phase === 'checking' ? 'checking' : check.phase, attemptsLeft: check.attemptsLeft, message: check.message }
}

/** Выбор кассира «Начала работы»: выбранный в этом заходе, иначе прошлый; единственный кассир выбирается сам. */
function pickCashier(list: readonly AqsiPosCashiers['cashiers'][number][], cashiersReady: boolean, memory: StartMemory): { cashier: StartVm['cashier']; resetNote: string | null } {
  const remembered = memory.draft.cashier ?? memory.last.cashier
  let cashier: StartVm['cashier'] = null
  let resetNote: string | null = null
  if (cashiersReady) {
    if (remembered?.kind === 'cashier') {
      const found = list.find((item) => item.cashierId.toLowerCase() === remembered.id.toLowerCase())
      if (found !== undefined) cashier = { kind: 'cashier', id: found.cashierId, name: found.name, login: loginKindOf() }
      else resetNote = missingCashierNote(remembered.name)
    } else if (remembered?.kind === 'own') {
      if (list.length === 0) cashier = { kind: 'own' }
      else resetNote = 'В кабинете появились кассиры — выберите кассира.'
    }
    const only = list.length === 1 ? list[0] : undefined
    if (cashier === null && only !== undefined) {
      cashier = { kind: 'cashier', id: only.cashierId, name: only.name, login: loginKindOf() }
    }
  }
  return { cashier, resetNote }
}

/** Кассир кабинета, выбранный в «Начале работы»: по нему читаются его настройки РМК; `null` — не выбран или работа под своим именем. */
export function startCashierIdOf(cashiers: AqsiPosCashiers | undefined, memory: StartMemory): string | null {
  if (cashiers === undefined) return null
  const { cashier } = pickCashier(cashiers.cashiers, true, memory)
  return cashier?.kind === 'cashier' ? cashier.id : null
}

/**
 * «Начало работы» (RP-FB22 3): что выбрано и какие блоки открыты. Следующий блок открывается, когда готов предыдущий;
 * готовый сворачивается в строку «изменить». Вид цены всегда имеет значение (последний у этого кассира), поэтому
 * блок «Вид цены» готов сразу — его можно не трогать. Всё, что решает сервер (есть ли у кассира код, свободна ли касса),
 * берётся из его ответа, а не из вида источника.
 */
export function buildStart(input: StartInput): StartVm {
  const { resourceId, memory, access } = input
  const cashiersReady = isReady(input.cashiersView) && input.cashiers !== undefined
  const list = readyData(input.cashiersView, input.cashiers)?.cashiers ?? []
  const lastCashierId = memory.last.cashier?.kind === 'cashier' ? memory.last.cashier.id : null
  const { cashier, resetNote } = pickCashier(list, cashiersReady, memory)

  const selectedCashierId = cashier?.kind === 'cashier' ? cashier.id : null
  const options = cashiersReady && input.cashiers !== undefined ? cashierOptions(input.cashiers, selectedCashierId, lastCashierId) : []
  const code = cashier?.kind === 'cashier' ? codeOf(resourceId, cashier, access) : null

  // Вид цены: выбранный в этом заходе, иначе последний у этого кассира на этом устройстве, иначе «Цена кассы».
  const cashierKey = cashierKeyOf(cashier === null ? null : cashier.kind === 'own' ? { kind: 'own' } : { kind: 'cashier', id: cashier.id, name: cashier.name })
  // Персональное значение кассира сильнее «как в прошлый раз», но слабее выбора, сделанного в этом заходе.
  const personalPriceId = input.rmk?.defaultPriceTypeResourceId != null && input.rmk.defaultPriceTypeKey != null
    ? `${input.rmk.defaultPriceTypeResourceId}/${input.rmk.defaultPriceTypeKey}` : null
  const fromPersonal = memory.draft.priceTypeId === null && personalPriceId !== null
  const wanted = memory.draft.priceTypeId ?? personalPriceId ?? memory.last.priceTypes[cashierKey] ?? PRICE_CASH
  const known = input.price.options.find((option) => option.id === wanted)
  const priceKnown = known !== undefined || !input.price.ready
  const priceTypeId = priceKnown ? wanted : PRICE_CASH
  const priceTypeResetNote = priceKnown || wanted === PRICE_CASH ? null
    : fromPersonal ? 'Назначенный вам вид цены сейчас недоступен — выбрана «Цена кассы», вы можете выбрать другой.'
      : 'Прежний вид цены больше не доступен — выбрана «Цена кассы».'
  const priceOptions: PriceTypeOptionVm[] = input.price.options.map((option) => ({ ...option, selected: option.id === priceTypeId }))
  if (!priceOptions.some((option) => option.selected)) {
    priceOptions.push({ id: priceTypeId, name: 'Выбранный вид цены', hint: 'список видов цен читается…', selected: true })
  }
  const priceTypeName = priceOptions.find((option) => option.selected)?.name ?? 'Цена кассы'

  // Касса: выбранная в этом заходе; иначе та, с которой работали; одна доступная — выбрана сама.
  const registersReady = isReady(input.registersQuery)
  const personalDevice = input.rmk?.defaultRegisterId != null && /^[0-9]+$/.test(input.rmk.defaultRegisterId) ? Number(input.rmk.defaultRegisterId) : null
  const chosenDevice = memory.draft.deviceId ?? personalDevice ?? memory.last.deviceId
  const chosenRow = chosenDevice === null ? undefined : input.rows.find((row) => row.deviceId === chosenDevice)
  let selected: RegisterRowVm | null = chosenRow?.selectable === true ? chosenRow : null
  if (selected === null && memory.draft.deviceId === null && registersReady) {
    const available = input.rows.filter((row) => row.selectable)
    if (available.length === 1) selected = available[0] ?? null
  }

  // Назначенная касса недоступна (занята, отключена, удалена) — не подменяем молча «прошлой»: человеку сказано, выбор за ним.
  const registerNote = memory.draft.deviceId === null && personalDevice !== null && registersReady && selected?.deviceId !== personalDevice
    ? 'Назначенная вам касса сейчас недоступна — выберите другую кассу.' : null
  const cashierDone = cashier !== null
  const codeDone = code === null || code.state === 'verified'
  const steps: StartVm['steps'] = {
    cashier: cashierDone ? 'done' : 'open',
    code: cashier === null || code === null ? null : code.state === 'verified' ? 'done' : 'open',
    price: cashierDone && codeDone ? 'done' : 'later',
    register: !cashierDone || !codeDone ? 'later' : selected !== null ? 'done' : 'open',
  }

  let blockedReason: string | null = null
  if (!cashiersReady) blockedReason = 'Список кассиров ещё не прочитан.'
  else if (cashier === null) {
    blockedReason = list.length === 0 ? 'Выберите «Работать под своим именем» или заведите кассиров в кабинете.' : 'Выберите кассира.'
  } else if (code !== null && code.state !== 'verified') {
    blockedReason =
      code.state === 'locked'
        ? 'Кассир заблокирован: слишком много неверных кодов. Подождите или выберите другого кассира.'
        : 'Введите код кассира.'
  } else if (!registersReady) blockedReason = 'Список касс ещё не прочитан.'
  else if (selected === null) blockedReason = input.rows.some((row) => row.selectable) ? 'Выберите кассу.' : 'Свободных касс нет.'
  else if (input.busy) blockedReason = 'Идёт другая команда — дождитесь ответа.'

  return {
    cashiers: {
      query: input.cashiersView,
      options,
      empty: cashiersReady && list.length === 0,
      searchable: list.length > CASHIER_SEARCH_FROM,
    },
    cashier,
    resetNote,
    code,
    priceTypes: { query: input.price.query, options: priceOptions },
    priceTypeName,
    priceTypeId,
    priceTypeResetNote,
    registerNote,
    registers: { query: input.registersQuery, rows: input.rows, selected },
    steps,
    action: selected?.primary === 'continue' ? 'continue' : 'open',
    canStart: blockedReason === null,
    blockedReason,
  }
}

/**
 * Идёт ли ожидание ответа кабинета на «Открыть смену»: команда принята, а смена в нашей работе ещё не появилась.
 * Тот же вопрос задаёт и сам клиент, когда команда ещё отправляется: экран ожидания один.
 */
export function isOpeningWait(operation: OperationVm | null, mine: MineVm | null, shift: ShiftVm | null): boolean {
  if (operation === null || mine === null || operation.kind !== 'open') return false
  if (!operation.canDismiss) return true
  return operation.phase === 'settled' && shift?.isOpen === true && mine.shiftNumber !== shift.number && operation.tone === 'success'
}

const CONFIRM_HINT = 'Если касса просит подтверждение, подтвердите действие на её экране.'

/**
 * Экран ожидания открытия смены (RP-FB22 3): строки хода «Проверяем связь → Отправили команду кассе → Ждём ответ
 * кассы». Ход ведёт клиент, пока команда не принята, затем правду о ходе говорит операция на сервере.
 */
export function buildOpening(
  entry: OpeningEntry | undefined,
  mine: MineVm | null,
  operation: OperationVm | null,
  shift: ShiftVm | null,
): OpeningVm | null {
  const waiting = isOpeningWait(operation, mine, shift)
  if (entry === undefined && !waiting) return null
  const mode = entry?.mode ?? 'open'
  const accepted = waiting && operation !== null && operation.phase !== 'sending'
  const sending = entry?.phase === 'send' || (waiting && operation !== null && operation.phase === 'sending')
  const linkStep: OpeningStepVm = {
    id: 'link',
    text: !accepted && !sending ? 'Проверяем связь с кассой' : 'Связь с кассой проверена',
    state: !accepted && !sending ? 'active' : 'done',
  }
  const steps: OpeningStepVm[] = [linkStep]
  if (mode === 'continue') {
    steps.push({
      id: 'send',
      text: sending ? 'Садимся за кассу и принимаем смену' : 'Сядем за кассу и примем смену',
      state: sending ? 'active' : 'pending',
    })
  } else {
    steps.push({
      id: 'send',
      text: accepted ? 'Команда кассе отправлена' : sending ? 'Отправляем команду кассе' : 'Отправим команду кассе',
      state: accepted ? 'done' : sending ? 'active' : 'pending',
    })
    steps.push({
      id: 'wait',
      text: accepted ? 'Ждём ответ кассы' : 'Дождёмся ответа кассы',
      state: accepted ? 'active' : 'pending',
    })
  }
  const settledOk = waiting && operation !== null && operation.phase === 'settled'
  return {
    mode,
    registerName: entry?.registerName ?? mine?.registerName ?? 'Касса',
    cashierName: entry?.cashierName ?? null,
    startedAt: entry?.startedAt ?? operation?.startedAt ?? new Date(0).toISOString(),
    steps: settledOk ? steps.map((step) => ({ ...step, state: 'done' as const })) : steps,
    hint: settledOk ? null : mode === 'open' && (sending || accepted) ? CONFIRM_HINT : null,
  }
}
